import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/base";
import { fmtRating, track } from "@/lib/api";
import { tic } from "@/lib/sound";
import { Bars, LockedZone, OpenZone, ZoneCounter } from "@/components/zones/ZoneParts";
import { isTrade } from "@/components/zones/Podium";
import StickyUnlock from "@/components/zones/StickyUnlock";

const POINTS = ["Catégorie", "Description", "Photos", "Avis", "Site"];
const fmtVol = (v) => (v ? `${v.toLocaleString("fr-FR")} / mois` : "Peu de recherches");
const reduced = () => !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
const playedKey = (id) => `archijo-unlock-${id}`;

const QUEST = { Catégorie: "1.2", Description: "2.1", Photos: "2.3", Avis: "3.2", Site: "2.5" };

export function pointStatus(p, audit, data) {
  const u = data.user?.status === "found" ? data.user : null;
  const ok = (b, n) => (b ? "OK" : `À corriger (+${n})`);
  if (p === "Catégorie") return ok(audit.q1 === "oui", 8);
  if (p === "Description") return ok(audit.q3 === "oui", 5);
  if (p === "Photos") return ok(audit.q2 === "oui" || (u && u.photos >= 10), 5);
  const top = data.top3 || [];
  if (!u) return "Jo en parle avec toi";
  if (p === "Avis") return u.reviews >= top.reduce((a, c) => a + (c.reviews || 0), 0) / Math.max(1, top.length) ? "OK" : "À travailler";
  return u.has_site ? "OK" : "À corriger";
}

export function Zone2Rows({ rows }) {
  return (
    <ul className="flex flex-col" data-testid="zone-2-rows">
      {rows.map((c) => (
        <li key={c.rank} className="flex gap-3 border-t border-line py-2.5 first:border-t-0 first:pt-0" data-testid={`zone-2-row-${c.rank}`}>
          <span className="num grid h-7 w-7 shrink-0 place-items-center rounded-full bg-surface-2 text-[13px] font-medium text-ink">{c.rank}</span>
          <div className="min-w-0 flex-1">
            <p className="line-clamp-2 break-words text-[14px] font-medium leading-snug text-ink" data-testid={`zone-2-name-${c.rank}`}>
              {c.name}
            </p>
            <p className="num mt-0.5 text-[12px] text-muted" data-testid={`zone-2-stats-${c.rank}`}>
              <span className="text-ink">{c.reviews}</span> avis · note <span className="text-ink">{fmtRating(c.rating)}</span> · <span className="text-ink">{c.photos >= 10 ? "10+" : c.photos ?? 0}</span> photos
            </p>
            <p className="mt-0.5 text-[12px] leading-snug text-muted" data-testid={`zone-2-details-${c.rank}`}>
              {[isTrade(c.category) ? c.category : null, `site ${c.has_site ? "oui" : "non"}`, `horaires ${c.has_hours ? "oui" : "non"}`].filter(Boolean).join(" · ")}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

const normKw = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, " ").trim();
// Same normalised keyword = one line (first occurrence kept).
export const dedupeKw = (items) => items.filter((k, i, a) => a.findIndex((x) => normKw(x.keyword) === normKw(k.keyword)) === i);

export function KeywordRows({ items, locked }) {
  return (
    <ul className="flex flex-col" data-testid={locked ? "zone-4-preview" : "zone-4-rows"}>
      {dedupeKw(items).map((k, i) => (
        <li key={`${k.keyword}-${i}`} className="flex items-center justify-between gap-3 border-t border-line py-2.5 first:border-t-0 first:pt-0" data-testid={`zone-4-kw-${i}`}>
          <span className="min-w-0">
            <span className="block truncate text-[14px] text-ink">{k.keyword}</span>
            {k.badges && <span className="mt-0.5 block text-[11px] text-muted">{k.badges.join(" · ")}</span>}
          </span>
          <span className="num shrink-0 text-[13px] text-ink" data-testid={`zone-4-vol-${i}`}>
            {k.hidden ? "••• / mois" : fmtVol(k.volume)}
          </span>
        </li>
      ))}
    </ul>
  );
}

export default function Zones({ data, onUnlock: rawUnlock, audit, animate = false }) {
  const interest = (target) => () => {
    track("unlock_interest", { target, radar_id: data.radar_id });
    rawUnlock?.();
  };
  const onUnlock = interest("unlock_button");
  const navigate = useNavigate();
  const unlocked = !!data.owned;
  const ville = data.ville?.nom;
  const shouldPlay = unlocked && animate && !reduced() && (() => {
    try {
      return !localStorage.getItem(playedKey(data.radar_id));
    } catch {
      return false;
    }
  })();
  const [opened, setOpened] = useState(shouldPlay ? 0 : 3);
  useEffect(() => {
    if (!unlocked) return undefined;
    try {
      localStorage.setItem(playedKey(data.radar_id), "1");
    } catch {}
    if (!shouldPlay) return undefined;
    const ts = [1, 2, 3].map((i) => setTimeout(() => { tic(); setOpened(i); }, 450 + (i - 1) * 300));
    return () => ts.forEach(clearTimeout);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const open = (i) => unlocked && opened >= i;
  const t2 = "Zone 2 · Les rangs 4 à 10";
  const t3 = "Zone 3 · Ton diagnostic de fiche";
  const t4 = `Zone 4 · Tes mots-clés à ${ville}`;
  const kw = data.keywords || {};
  const preview = [kw.main && { ...kw.main }, ...(kw.secondaries || []).map((s) => ({ ...s, hidden: true }))].filter(Boolean);

  return (
    <div className="flex flex-col gap-4" data-testid="zones" data-opened={unlocked ? opened : 0}>
      <ZoneCounter count={1 + (unlocked ? opened : 0)} />
      {open(1) ? (
        <OpenZone n={2} title={t2} opening={shouldPlay}>
          <Zone2Rows rows={(data.top10 || []).slice(3)} />
        </OpenZone>
      ) : (
        <LockedZone n={2} title={t2} line="Les 7 concurrents juste derrière le podium, avec leurs avis, leur note et leurs photos." onOpen={interest("zone2")}>
          <Bars />
        </LockedZone>
      )}
      {open(2) ? (
        <OpenZone n={3} title={t3} opening={shouldPlay}>
          {audit ? (
            <ul className="flex flex-col" data-testid="zone-3-results">
              {POINTS.map((p) => (
                <li key={p} className="border-t border-line text-[14px] first:border-t-0">
                  {pointStatus(p, audit, data) === "OK" ? (
                    <span className="flex justify-between gap-3 py-2">
                      <span className="text-ink">{p}</span>
                      <span className="text-muted" data-testid={`zone-3-status-${p}`}>OK</span>
                    </span>
                  ) : (
                    <button type="button" onClick={() => navigate(`/app/quetes?q=${QUEST[p]}`)} data-testid={`zone-3-quest-${p}`} className="flex w-full justify-between gap-3 py-2 text-left hover:underline">
                      <span className="text-ink">{p}</span>
                      <span className="text-ink" data-testid={`zone-3-status-${p}`}>{pointStatus(p, audit, data)}</span>
                    </button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <>
              <p className="text-[14px] text-ink" data-testid="zone-3-intro">Jo vérifie ces points avec toi</p>
              <p className="mt-1 text-[13px] text-muted">{POINTS.join(" · ")}</p>
            </>
          )}
          <Button onClick={() => navigate(audit ? "/app/quetes?rec=1" : "/app")} className="mt-4 w-full sm:w-auto" data-testid="zone-3-jo-btn">
            {audit ? "Voir avec Jo" : "Commencer avec Jo"}
          </Button>
        </OpenZone>
      ) : (
        <LockedZone n={3} title={t3} line="Les points qui te font perdre des places, vérifiés un par un : catégorie, description, photos, avis, site." onOpen={interest("zone3")}>
          <ul className="flex flex-col gap-2.5" aria-hidden="true">
            {POINTS.map((p) => (
              <li key={p} className="flex items-center justify-between gap-3 text-[14px]">
                <span className="text-ink">{p}</span>
                <span className="h-2.5 w-24 select-none rounded-full bg-line blur-[3px]" />
              </li>
            ))}
          </ul>
        </LockedZone>
      )}
      {open(3) ? (
        <OpenZone n={4} title={t4} opening={shouldPlay}>
          <KeywordRows items={kw.items || []} />
        </OpenZone>
      ) : (
        <LockedZone n={4} title={t4} line="Les recherches que tapent tes futurs clients, avec leur volume mensuel." onOpen={interest("zone4")}>
          <KeywordRows items={preview} locked />
        </LockedZone>
      )}
      {!unlocked && <StickyUnlock onUnlock={onUnlock} />}
    </div>
  );
}
